"use client";

/**
 * <AbilityConnections> — the ability page's "The connections" chapter,
 * rendering the ability's OWN bundled MCPs (`catalogDetail.mcps`, from
 * ability_mcps) with their real per-user status.
 *
 * Replaces the global <McpList> that used to sit here (founder report,
 * 2026-08-12): McpList reads `GET /api/items/mcp/mine` — the user's My Kit
 * connections — with no ability parameter, so every ability page showed the
 * same unrelated list (filesystem / memory / notion…) while the ability's own
 * MCP (higgsfield) never appeared, even with a LIVE OAuth token in custody.
 * The count cell above the chapter already used `catalogDetail.mcps`; this
 * component makes the chapter agree with it.
 *
 * Status truthfulness: an `auth: 'oauth'` row probes the user's token custody
 * (`probeMcpOAuthConnected`) — `connected` / `not connected`, with a Connect
 * action that mints a FRESH authorize URL at click time (the stored-URL PKCE
 * state expires — the Composio dead-link lesson) via `mintMcpOAuthUrl`, then
 * settles through useAuthConnectState's probe-on-tab-return. Non-OAuth rows
 * are configuration, not per-user grants — they read `included`.
 *
 * No Remove control: these rows are the ABILITY's bundle, not user items —
 * they leave with "Uninstall this ability", and a per-row Remove here would
 * have deleted unrelated My Kit connections under the old list.
 */

import { memo, useCallback, useEffect, useState, type ReactNode } from "react";
import type { CatalogAbilityDetail } from "@bash/shared";
import { useAuthConnectState } from "@/hooks/useAuthConnectState";
import {
  probeMcpOAuthConnected,
  mintMcpOAuthUrl,
} from "@/lib/mcp-oauth-status";
import { integrationLogoUrl } from "@/lib/abilities/integration-logo";
import { PlugConnectionIcon } from "@/components/icons/PlugConnectionIcon";

type AbilityMcp = CatalogAbilityDetail["mcps"][number];

export interface AbilityConnectionsProps {
  mcps: AbilityMcp[];
  /** Chapter head — the page owns the copy, same contract as McpList. */
  header?: ReactNode;
  /** Row-count report for the page's last-chapter computation. */
  onCount?: (total: number) => void;
}

/** Brand coin — same species as McpList's ConnCoin (CDN logo → gilded plug). */
function Coin({ slug, name }: { slug?: string; name: string }) {
  const [fallback, setFallback] = useState(false);
  if (!slug || fallback) {
    return (
      <span
        className="ab-chip-ico w-10 h-10 rounded-12 flex items-center justify-center shrink-0 text-5 leading-none [&_img]:w-5.5 [&_img]:h-5.5 [&_img]:block bg-gold-molten-wash border border-gold-line-subtle-20"
        aria-hidden
      >
        <PlugConnectionIcon />
      </span>
    );
  }
  return (
    <span
      className="ab-chip-ico ab-chip-ico--plate w-10 h-10 rounded-12 bg-bg-base border border-border-subtle flex items-center justify-center shrink-0 text-5 leading-none [&_img]:w-5.5 [&_img]:h-5.5 [&_img]:block"
      aria-hidden
    >
      {/* eslint-disable-next-line @next/next/no-img-element -- CDN brand mark; ConnCoin precedent */}
      <img
        src={integrationLogoUrl(slug)}
        alt={name}
        onError={() => setFallback(true)}
      />
    </span>
  );
}

/** OAuth row — its own component so the status hook runs per OAuth row only. */
function OauthRow({ mcp }: { mcp: AbilityMcp & { slug: string } }) {
  const { status, markClicked, resetToIdle } = useAuthConnectState(
    mcp.slug,
    probeMcpOAuthConnected
  );

  const handleConnect = useCallback(async () => {
    markClicked();
    try {
      // Fresh authorize URL at click time; the stored one's PKCE state expires.
      const { authUrl } = await mintMcpOAuthUrl(mcp.slug);
      if (authUrl) {
        const popup = window.open(authUrl, "_blank");
        if (!popup) resetToIdle(); // popup blocked — don't sit on the waiting state
      } else {
        resetToIdle(); // already connected / manual setup — the probe settles it
      }
    } catch {
      resetToIdle();
    }
  }, [mcp.slug, markClicked, resetToIdle]);

  return (
    <div className="flex items-center gap-3.5 py-3.5 px-0.5 border-b border-border-subtle last:[border-bottom:none] hover:[&_.abd-conn\_\_remove]:opacity-100 can-hover:[&:focus-within_.abd-conn\_\_remove]:opacity-100 [&_.ab-chip-ico]:w-8.5 [&_.ab-chip-ico]:h-8.5 [&_.ab-chip-ico]:rounded-10 [&_.ab-chip-ico_img]:w-4.5 [&_.ab-chip-ico_img]:h-4.5 [&_.ab-chip-ico_svg]:w-4.5 [&_.ab-chip-ico_svg]:h-4.5 [&_.ab-chip-ico_svg]:[fill:none] [&_.ab-chip-ico_svg]:stroke-gold-ink">
      <Coin slug={mcp.slug} name={mcp.name} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline flex-wrap gap-2.5">
          <span className="text-3.5 font-medium text-fg-default [overflow-wrap:anywhere]">
            {mcp.name}
          </span>
          {status === "connected" && (
            <span className="inline-flex items-center gap-2 shrink-0 font-mono text-3 tracking-wider text-gold-ink">
              <i className="w-1.5 h-1.5 rounded-full shrink-0 bg-gold-dot animate-lib-breathe motion-reduce:animate-none" />
              connected
            </span>
          )}
          {status === "connecting" && (
            <span className="inline-flex items-center gap-2 shrink-0 font-mono text-3 tracking-wider text-gold-ink">
              waiting for the other tab…
            </span>
          )}
          {status === "idle" && (
            <button
              type="button"
              className="shrink-0 font-sans text-3 text-fg-subtle bg-bg-elevated border border-border-default rounded-100 py-1 px-3 cursor-pointer transition-colors hover:text-red hover:border-red-line-40 disabled:opacity-56 disabled:cursor-not-allowed can-hover:opacity-0 can-hover:transition-[opacity,color,border-color] duration-150 ease-out"
              onClick={handleConnect}
            >
              Connect
            </button>
          )}
        </div>
        <div className="flex items-center flex-wrap gap-2 mt-1 font-mono text-3 tracking-[0.02em] text-fg-subtle">
          <span>{mcp.slug}</span>
          <span>{mcp.transport}</span>
        </div>
      </div>
    </div>
  );
}

export const AbilityConnections = memo(function AbilityConnections({
  mcps,
  header,
  onCount,
}: AbilityConnectionsProps) {
  // Report the count post-render (a parent setState during child render is a
  // React violation); data is synchronous so this settles on the first commit.
  useEffect(() => {
    onCount?.(mcps.length);
  }, [mcps.length, onCount]);
  if (mcps.length === 0) return null; // chapter hides itself, McpList contract

  return (
    <section className="animate-skill-enter-1 motion-reduce:animate-none [animation-delay:240ms]">
      {header}
      <div className="flex flex-col">
        {mcps.map((mcp) =>
          mcp.auth === "oauth" && mcp.slug ? (
            <OauthRow
              key={mcp.slug}
              mcp={mcp as AbilityMcp & { slug: string }}
            />
          ) : (
            <div
              key={mcp.slug ?? mcp.name}
              className="flex items-center gap-3.5 py-3.5 px-0.5 border-b border-border-subtle last:[border-bottom:none] hover:[&_.abd-conn\_\_remove]:opacity-100 can-hover:[&:focus-within_.abd-conn\_\_remove]:opacity-100 [&_.ab-chip-ico]:w-8.5 [&_.ab-chip-ico]:h-8.5 [&_.ab-chip-ico]:rounded-10 [&_.ab-chip-ico_img]:w-4.5 [&_.ab-chip-ico_img]:h-4.5 [&_.ab-chip-ico_svg]:w-4.5 [&_.ab-chip-ico_svg]:h-4.5 [&_.ab-chip-ico_svg]:[fill:none] [&_.ab-chip-ico_svg]:stroke-gold-ink"
            >
              <Coin slug={mcp.slug} name={mcp.name} />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline flex-wrap gap-2.5">
                  <span className="text-3.5 font-medium text-fg-default [overflow-wrap:anywhere]">
                    {mcp.name}
                  </span>
                  {/* Bundled configuration, not a per-user grant — say so. */}
                  <span className="inline-flex items-center gap-2 shrink-0 font-mono text-3 tracking-wider text-gold-ink">
                    <i className="w-1.5 h-1.5 rounded-full shrink-0 bg-gold-dot animate-lib-breathe motion-reduce:animate-none" />
                    included
                  </span>
                </div>
                <div className="flex items-center flex-wrap gap-2 mt-1 font-mono text-3 tracking-[0.02em] text-fg-subtle">
                  {mcp.slug && <span>{mcp.slug}</span>}
                  <span>{mcp.transport}</span>
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </section>
  );
});
